<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>
  <div class="page-wrapper" :class="{ 'dq-platform': isSuperAdminRoute }">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="head-icons ms-2">
          <a
            href="javascript:void(0);"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-original-title="Collapse"
            id="collapse-header"
            @click="toggleHeader"
          >
            <i class="ti ti-chevrons-up"></i>
          </a>
        </div>
      </div>

      <settings-tab v-if="!isSuperAdminRoute"></settings-tab>

      <div class="row">
        <others-sidebar v-if="!isSuperAdminRoute"></others-sidebar>
        <div :class="isSuperAdminRoute ? 'col-xl-12' : 'col-xl-9'">
          <div class="card border-0 shadow-sm dq-migration-hero mb-3">
            <div class="card-body p-4">
              <div class="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <span class="dq-kicker">Migration Center</span>
                  <h3 class="mb-2">Move a company into DoneQuick carefully</h3>
                  <p class="text-muted mb-0">
                    <template v-if="isSuperAdmin">
                      Preview data before commit, keep everything locked to the target company,
                      and run assisted legacy migrations in the right order.
                    </template>
                    <template v-else>
                      Use this page for approved CSV and Excel imports only. Legacy Access database
                      migrations are handled by superadmin.
                    </template>
                  </p>
                </div>
                <div class="dq-order-card">
                  <div class="fw-semibold mb-2">Recommended order</div>
                  <div class="small text-muted">1. Departments</div>
                  <div class="small text-muted">2. Designations</div>
                  <div class="small text-muted">3. Employees</div>
                  <div class="small text-muted">4. Employee Salaries</div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isSuperAdmin" class="card mb-3">
            <div class="card-header">
              <h4 class="mb-0">Pay100 Access Migration</h4>
            </div>
            <div class="card-body">
              <div class="alert alert-light border mb-3">
                <div class="fw-semibold mb-1">Internal migration workflow</div>
                <div class="text-muted small">
                  Use this for clients coming from the standard Pay100 Access database. Extract first,
                  review the staging artifacts, then preview and import the mapped datasets below.
                </div>
              </div>

              <div class="row g-3 mb-3">
                <div v-if="isSuperAdmin" class="col-md-12">
                  <label class="form-label">Target Company</label>
                  <select v-model="targetCompanyId" class="form-select">
                    <option :value="null">Select company to receive the migration</option>
                    <option v-for="company in companyOptions" :key="company.id" :value="company.id">
                      {{ company.company_name }}
                    </option>
                  </select>
                  <div class="small text-muted mt-2">
                    Extraction, preview, and import will run against this selected company only.
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Main Data Database</label>
                  <label class="dq-dropzone dq-dropzone--stack w-100 mb-0" :class="{ 'is-ready': pay100DataFileName }">
                    <div class="dq-dropzone-title">Upload main `.mdb`</div>
                    <div class="dq-dropzone-help">
                      {{ pay100DataFileName || "BAK_Pay100XPDBT.mdb or equivalent" }}
                    </div>
                    <input class="d-none" type="file" accept=".mdb" @change="onPay100FileChange($event, 'data')" />
                  </label>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Kenya Tax Database</label>
                  <label class="dq-dropzone dq-dropzone--stack w-100 mb-0" :class="{ 'is-ready': pay100TaxFileName }">
                    <div class="dq-dropzone-title">Upload tax `.mdb`</div>
                    <div class="dq-dropzone-help">
                      {{ pay100TaxFileName || "Pay100XPTax_Ke.mdb or equivalent" }}
                    </div>
                    <input class="d-none" type="file" accept=".mdb" @change="onPay100FileChange($event, 'tax')" />
                  </label>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Access Password</label>
                  <input
                    v-model="pay100Password"
                    type="password"
                    class="form-control"
                    placeholder="Enter Access database password"
                  />
                  <div class="small text-muted mt-2">
                    The password is used only to extract staging data on the server.
                  </div>
                </div>
              </div>

              <div class="d-flex flex-wrap gap-2 mb-3">
                <button
                  class="btn btn-warning"
                  type="button"
                  :disabled="!canExtractPay100 || pay100ExtractLoading"
                  @click="extractPay100"
                >
                  <span v-if="pay100ExtractLoading" class="spinner-border spinner-border-sm me-2"></span>
                  Extract Pay100 Data
                </button>
                <button
                  v-if="pay100ExtractSummary"
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="clearPay100Extraction"
                >
                  Reset Extraction
                </button>
              </div>

              <div v-if="pay100ExtractSummary" class="row g-3 mb-3">
                <div class="col-md-4" v-for="card in pay100SummaryCards" :key="card.label">
                  <div class="dq-summary-card">
                    <div class="dq-summary-label">{{ card.label }}</div>
                    <div class="dq-summary-value">{{ card.value }}</div>
                  </div>
                </div>
              </div>

              <div v-if="hasSourceCompanyProfile" class="card border mb-3">
                <div class="card-body">
                  <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                    <div>
                      <div class="fw-semibold">Source Company Details</div>
                      <div class="small text-muted">
                        Extracted from `AA_Company_Details`. Review before applying to the selected target company.
                      </div>
                    </div>
                    <button
                      class="btn btn-sm btn-outline-primary"
                      type="button"
                      :disabled="!effectiveCompanyId || applyingSourceCompanyProfile || !selectedComparisonKeys.length"
                      @click="applySourceCompanyProfile"
                    >
                      <span
                        v-if="applyingSourceCompanyProfile"
                        class="spinner-border spinner-border-sm me-2"
                      ></span>
                      Apply To Target Company
                    </button>
                  </div>

                  <div class="row g-3">
                    <div class="col-md-4">
                      <div class="small text-muted">Company Name</div>
                      <div class="fw-semibold">{{ sourceCompanyProfile.company_name || "—" }}</div>
                    </div>
                    <div class="col-md-4">
                      <div class="small text-muted">Phone</div>
                      <div class="fw-semibold">{{ sourceCompanyProfile.phone || "—" }}</div>
                    </div>
                    <div class="col-md-4">
                      <div class="small text-muted">Email</div>
                      <div class="fw-semibold">{{ sourceCompanyProfile.email || "—" }}</div>
                    </div>
                    <div class="col-md-4">
                      <div class="small text-muted">Country</div>
                      <div class="fw-semibold">
                        {{ sourceCompanyProfile.country || "—" }}
                        <span v-if="sourceCompanyProfile.country_code" class="text-muted">
                          ({{ sourceCompanyProfile.country_code }})
                        </span>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="small text-muted">City / Region</div>
                      <div class="fw-semibold">{{ sourceCompanyProfile.state || "—" }}</div>
                    </div>
                    <div class="col-md-4">
                      <div class="small text-muted">Payroll Frequency</div>
                      <div class="fw-semibold">{{ sourceCompanyProfile.payroll_frequency || "—" }}</div>
                    </div>
                    <div class="col-md-8">
                      <div class="small text-muted">Address</div>
                      <div class="fw-semibold">{{ sourceCompanyProfile.address || "—" }}</div>
                    </div>
                    <div class="col-md-4">
                      <div class="small text-muted">Contact Person</div>
                      <div class="fw-semibold">
                        {{ sourceCompanyProfile.contact_name || "—" }}
                        <span v-if="sourceCompanyProfile.contact_position" class="text-muted">
                          ({{ sourceCompanyProfile.contact_position }})
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-if="effectiveCompanyId" class="mt-4">
                    <div class="fw-semibold mb-2">Field Comparison</div>
                    <div class="small text-muted mb-3">
                      Only checked fields will update the selected target company. Empty DoneQuick fields are
                      preselected automatically; conflicting non-empty values are left unchecked.
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm align-middle mb-0">
                        <thead class="thead-light">
                          <tr>
                            <th style="width: 90px">Apply</th>
                            <th>Field</th>
                            <th>Current DoneQuick Value</th>
                            <th>Source Access Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in companyProfileComparisonRows" :key="row.key">
                            <td>
                              <input
                                v-if="row.hasSourceValue"
                                v-model="companyProfileFieldSelection[row.key]"
                                class="form-check-input"
                                type="checkbox"
                              />
                            </td>
                            <td class="fw-semibold">{{ row.label }}</td>
                            <td :class="row.hasCurrentValue ? '' : 'text-muted'">
                              {{ row.currentDisplay }}
                            </td>
                            <td :class="row.hasSourceValue ? '' : 'text-muted'">
                              {{ row.sourceDisplay }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="pay100Artifacts.length" class="table-responsive">
                <table class="table align-middle">
                  <thead class="thead-light">
                    <tr>
                      <th>Artifact</th>
                      <th>Rows</th>
                      <th>File</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="artifact in pay100Artifacts" :key="artifact.artifact_path">
                      <td>
                        <div class="fw-semibold">{{ artifact.label }}</div>
                        <div class="small text-muted">
                          <span v-if="artifact.module">Maps to {{ labelForModule(artifact.module) }}</span>
                          <span v-else>Reference artifact</span>
                        </div>
                      </td>
                      <td>{{ artifact.row_count ?? "—" }}</td>
                      <td class="small text-muted">{{ artifact.file_name }}</td>
                      <td>
                        <div class="d-flex flex-wrap gap-2">
                          <button
                            v-if="artifact.can_preview"
                            class="btn btn-sm btn-primary"
                            type="button"
                            :disabled="previewLoading"
                            @click="previewPay100Artifact(artifact)"
                          >
                            Preview Import
                          </button>
                          <button
                            v-if="artifact.can_download"
                            class="btn btn-sm btn-outline-secondary"
                            type="button"
                            @click="downloadPay100Artifact(artifact)"
                          >
                            Download
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-else class="alert alert-light border mb-3">
            <div class="fw-semibold mb-1">Legacy payroll migrations are internal only</div>
            <div class="text-muted small mb-0">
              Pay100 and other Access database migrations are reviewed and executed by superadmin.
              Tenant admins can still use the manual import tools below for approved CSV or Excel datasets.
            </div>
          </div>

          <div class="card mb-3">
            <div class="card-header">
              <h4 class="mb-0">Manual CSV / Excel Import</h4>
            </div>
            <div class="card-body">
              <div v-if="isSuperAdmin" class="row g-3 mb-3">
                <div class="col-md-12">
                  <label class="form-label">Target Company</label>
                  <select v-model="targetCompanyId" class="form-select">
                    <option :value="null">Select company to receive the import</option>
                    <option v-for="company in companyOptions" :key="company.id" :value="company.id">
                      {{ company.company_name }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="row g-3 mb-3">
                <div class="col-md-6">
                  <label class="form-label">Dataset</label>
                  <select v-model="selectedModule" class="form-select">
                    <option value="">Select a dataset</option>
                    <option
                      v-for="entry in moduleEntries"
                      :key="entry.key"
                      :value="entry.key"
                    >
                      {{ entry.label }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Import Mode</label>
                  <select v-model="importMode" class="form-select">
                    <option v-for="mode in modes" :key="mode.value" :value="mode.value">
                      {{ mode.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div v-if="selectedModuleConfig" class="alert alert-light border mb-3">
                <div class="fw-semibold mb-1">{{ selectedModuleConfig.label }}</div>
                <div class="text-muted small mb-2">{{ selectedModuleConfig.description }}</div>
                <div class="small">
                  <span class="fw-semibold">Required columns:</span>
                  {{ selectedModuleConfig.required_headers.join(", ") }}
                </div>
                <div class="small">
                  <span class="fw-semibold">Accepted columns:</span>
                  {{ selectedModuleConfig.accepted_headers.join(", ") }}
                </div>
              </div>

              <div class="dq-dropzone mb-3" :class="{ 'is-ready': fileName }">
                <div>
                  <div class="dq-dropzone-title">Upload CSV or Excel file</div>
                  <div class="dq-dropzone-help">
                    {{ fileName || "Use the template first if the source format is unknown." }}
                  </div>
                </div>
                <div class="d-flex flex-wrap gap-2">
                  <button class="btn btn-outline-primary btn-sm" type="button" :disabled="!selectedModule" @click="downloadTemplate">
                    <i class="ti ti-download me-1"></i>Template
                  </button>
                  <label class="btn btn-primary btn-sm mb-0">
                    <i class="ti ti-upload me-1"></i>Choose File
                    <input class="d-none" type="file" accept=".csv,.xlsx,.xls" @change="onFileChange" />
                  </label>
                </div>
              </div>

              <div class="d-flex flex-wrap gap-2">
                <button
                  class="btn btn-success"
                  type="button"
                  :disabled="!canPreview || previewLoading"
                  @click="runPreview"
                >
                  <span v-if="previewLoading" class="spinner-border spinner-border-sm me-2"></span>
                  Preview Import
                </button>
                <button
                  v-if="previewBatch?.id"
                  class="btn btn-dark"
                  type="button"
                  :disabled="!canCommit || commitLoading"
                  @click="commitImport"
                >
                  <span v-if="commitLoading" class="spinner-border spinner-border-sm me-2"></span>
                  Confirm Import
                </button>
                <button
                  v-if="previewBatch"
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="clearPreview"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div v-if="previewBatch" class="card mb-3">
            <div class="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h4 class="mb-0">Preview</h4>
                <div class="small text-muted">{{ previewBatch.file_name }} • {{ labelForModule(previewBatch.module) }}</div>
              </div>
              <span class="badge" :class="statusClass(previewBatch.status)">{{ previewBatch.status }}</span>
            </div>
            <div class="card-body">
              <div v-if="previewBatch.errors?.length" class="alert alert-danger">
                <div class="fw-semibold mb-1">File-level issues</div>
                <div v-for="err in previewBatch.errors" :key="err">{{ err }}</div>
              </div>

              <div class="row g-3 mb-3">
                <div class="col-md-3" v-for="card in previewCards" :key="card.label">
                  <div class="dq-summary-card">
                    <div class="dq-summary-label">{{ card.label }}</div>
                    <div class="dq-summary-value">{{ card.value }}</div>
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-sm align-middle">
                  <thead class="thead-light">
                    <tr>
                      <th>Row</th>
                      <th>Status</th>
                      <th>Action</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!previewRows.length">
                      <td colspan="4" class="text-center py-3">No preview rows available.</td>
                    </tr>
                    <tr v-for="row in previewRows" :key="row.row_number">
                      <td>#{{ row.row_number }}</td>
                      <td>
                        <span class="badge" :class="row.status === 'valid' ? 'bg-success' : 'bg-danger'">
                          {{ row.status }}
                        </span>
                      </td>
                      <td>
                        <span class="badge" :class="actionClass(row.action)">
                          {{ row.action }}
                        </span>
                      </td>
                      <td>
                        <div v-if="row.errors?.length" class="small text-danger">
                          <div v-for="err in row.errors" :key="err">{{ err }}</div>
                        </div>
                        <div v-else class="small text-muted">
                          <span v-if="row.warnings?.length">{{ row.warnings.join(" ") }}</span>
                          <span v-else>{{ summarizeRow(row.data) }}</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="card mb-3">
            <div class="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
              <h4 class="mb-0">Role Permission Repair</h4>
            </div>
            <div class="card-body">
              <p class="text-muted small mb-3">
                After legacy migration or employee spreadsheet import, use this to apply the standard
                Employee, HR, and Client permission templates to this company&apos;s roles. Run it once
                employees have logins assigned, or after importing users from the old system.
              </p>

              <div v-if="!effectiveCompanyId" class="alert alert-light border mb-0">
                Select a target company above before repairing role permissions.
              </div>

              <template v-else>
                <div v-if="roleRepairResult" class="alert alert-success mb-3">
                  <div class="fw-semibold mb-1">{{ roleRepairResult.message }}</div>
                  <ul class="small mb-0 ps-3">
                    <li v-for="(count, role) in roleRepairResult.roles" :key="role">
                      {{ role }}: {{ count }} permission(s)
                    </li>
                  </ul>
                </div>

                <button
                  class="btn btn-outline-primary"
                  type="button"
                  :disabled="roleRepairLoading"
                  @click="repairRolePermissions"
                >
                  <span v-if="roleRepairLoading" class="spinner-border spinner-border-sm me-2"></span>
                  Repair Role Permissions
                </button>
              </template>
            </div>
          </div>

          <div class="card">
            <div class="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
              <h4 class="mb-0">Import History</h4>
              <button class="btn btn-outline-secondary btn-sm" type="button" @click="loadHistory">
                Refresh
              </button>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table align-middle mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Dataset</th>
                      <th>File</th>
                      <th>Status</th>
                      <th>Summary</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="historyLoading">
                      <td colspan="5" class="text-center py-3">Loading import history...</td>
                    </tr>
                    <tr v-else-if="!history.length">
                      <td colspan="5" class="text-center py-3">No imports recorded yet.</td>
                    </tr>
                    <tr v-for="item in history" :key="item.id">
                      <td>{{ labelForModule(item.module) }}</td>
                      <td>{{ item.file_name }}</td>
                      <td><span class="badge" :class="statusClass(item.status)">{{ item.status }}</span></td>
                      <td class="small text-muted">{{ summarizeHistory(item.summary) }}</td>
                      <td>{{ formatDateTime(item.created_at) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import migrationImportService from "@/services/settings/migration-import.service";
import { toast } from "@/utils/toast";
import { useAuthStore } from "@/stores/authStore";
import { useCompanyStore } from "@/stores/companyStore";

const title = "Migration Center";
const text = "Administration";
const text1 = "Migration Center";
const route = useRoute();
const authStore = useAuthStore();
const companyStore = useCompanyStore();

const modules = ref({});
const modes = ref([]);
const selectedModule = ref("");
const importMode = ref("create_only");
const selectedFile = ref(null);
const previewBatch = ref(null);
const history = ref([]);
const previewLoading = ref(false);
const commitLoading = ref(false);
const historyLoading = ref(false);
const pay100DataFile = ref(null);
const pay100TaxFile = ref(null);
const pay100Password = ref("");
const pay100ExtractLoading = ref(false);
const pay100ExtractSummary = ref(null);
const pay100Artifacts = ref([]);
const sourceCompanyProfile = ref(null);
const applyingSourceCompanyProfile = ref(false);
const roleRepairLoading = ref(false);
const roleRepairResult = ref(null);
const targetCompanyId = ref(null);
const companyProfileFieldSelection = ref({});
const companyProfileFields = [
  { key: "company_name", label: "Company Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Primary Phone" },
  { key: "phone_two", label: "Secondary Phone" },
  { key: "website_url", label: "Website" },
  { key: "address", label: "Address" },
  { key: "state", label: "State / Region" },
  { key: "country", label: "Country" },
  { key: "country_code", label: "Country Code" },
  { key: "zip_code", label: "Postal / ZIP" },
];

const moduleEntries = computed(() =>
  Object.entries(modules.value).map(([key, config]) => ({
    key,
    label: config.label,
    ...config,
  }))
);

const selectedModuleConfig = computed(() => modules.value[selectedModule.value] || null);
const fileName = computed(() => selectedFile.value?.name || "");
const pay100DataFileName = computed(() => pay100DataFile.value?.name || "");
const pay100TaxFileName = computed(() => pay100TaxFile.value?.name || "");
const isSuperAdmin = computed(() => !!authStore.isSuperAdmin);
const isSuperAdminRoute = computed(() => route.path.startsWith("/super-admin/"));
const companyOptions = computed(() => companyStore.companies || []);
const currentTargetCompany = computed(() => companyStore.company || null);
const effectiveCompanyId = computed(() => {
  if (isSuperAdmin.value) {
    return targetCompanyId.value ? Number(targetCompanyId.value) : null;
  }
  return companyStore.activeCompanyId || null;
});
const canPreview = computed(() => !!effectiveCompanyId.value && !!selectedModule.value && !!selectedFile.value);
const canExtractPay100 = computed(
  () =>
    !!effectiveCompanyId.value &&
    !!pay100DataFile.value &&
    !!pay100TaxFile.value &&
    !!pay100Password.value.trim()
);
const canCommit = computed(() => {
  const summary = previewBatch.value?.summary || {};
  return !!effectiveCompanyId.value && Number(summary.valid_rows || 0) > 0;
});
const previewRows = computed(() => (previewBatch.value?.preview_rows || []).slice(0, 25));
const previewCards = computed(() => {
  const s = previewBatch.value?.summary || {};
  return [
    { label: "Rows", value: s.total_rows || 0 },
    { label: "Valid", value: s.valid_rows || 0 },
    { label: "Create", value: s.create_rows || 0 },
    { label: "Update / Skip", value: `${s.update_rows || 0} / ${s.skip_rows || 0}` },
  ];
});
const pay100SummaryCards = computed(() => {
  const counts = pay100ExtractSummary.value?.counts || {};
  return [
    { label: "Company Profile", value: counts.company_profile || 0 },
    { label: "Departments", value: counts.departments || 0 },
    { label: "Employees", value: counts.employees || 0 },
    { label: "Current Salaries", value: counts.employee_salaries_current || 0 },
  ];
});
const hasSourceCompanyProfile = computed(() =>
  !!sourceCompanyProfile.value &&
  Object.entries(sourceCompanyProfile.value).some(
    ([key, value]) => key !== "raw" && value !== null && value !== ""
  )
);
const companyProfileComparisonRows = computed(() =>
  companyProfileFields.map((field) => {
    const currentValue = currentTargetCompany.value?.[field.key];
    const sourceValue = sourceCompanyProfile.value?.[field.key];

    return {
      ...field,
      currentValue,
      sourceValue,
      currentDisplay: formatComparisonValue(currentValue),
      sourceDisplay: formatComparisonValue(sourceValue),
      hasCurrentValue: hasComparableValue(currentValue),
      hasSourceValue: hasComparableValue(sourceValue),
    };
  })
);
const selectedComparisonKeys = computed(() =>
  Object.entries(companyProfileFieldSelection.value)
    .filter(([, selected]) => !!selected)
    .map(([key]) => key)
);

function toggleHeader() {
  document.body.classList.toggle("header-collapse");
}

function onFileChange(event) {
  selectedFile.value = event.target.files?.[0] || null;
}

function onPay100FileChange(event, type) {
  const file = event.target.files?.[0] || null;
  if (type === "data") {
    pay100DataFile.value = file;
  } else {
    pay100TaxFile.value = file;
  }
}

async function loadMeta() {
  const res = await migrationImportService.meta();
  modules.value = res.data?.modules || {};
  modes.value = res.data?.modes || [];
}

async function loadHistory() {
  if (!effectiveCompanyId.value) {
    history.value = [];
    return;
  }
  historyLoading.value = true;
  try {
    const res = await migrationImportService.history(effectiveCompanyId.value);
    history.value = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load import history.", "error");
  } finally {
    historyLoading.value = false;
  }
}

async function loadTargetCompany() {
  if (!effectiveCompanyId.value) {
    companyStore.company = null;
    companyProfileFieldSelection.value = {};
    return;
  }

  try {
    await companyStore.fetchCompany(effectiveCompanyId.value, effectiveCompanyId.value);
    initializeCompanyProfileSelections();
  } catch (e) {
    companyProfileFieldSelection.value = {};
  }
}

async function downloadTemplate() {
  if (!selectedModule.value) return;
  if (!effectiveCompanyId.value) {
    toast("Select a target company first.", "error");
    return;
  }
  try {
    const res = await migrationImportService.downloadTemplate(
      selectedModule.value,
      effectiveCompanyId.value
    );
    const blob = new Blob([res.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedModule.value}-template.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to download template.", "error");
  }
}

async function runPreview() {
  if (!canPreview.value) return;
  previewLoading.value = true;
  try {
    const res = await migrationImportService.preview({
      module: selectedModule.value,
      import_mode: importMode.value,
      file: selectedFile.value,
      companyId: effectiveCompanyId.value,
    });
    previewBatch.value = res.data;
    toast("Preview generated.", "success");
    await loadHistory();
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to generate preview.", "error");
  } finally {
    previewLoading.value = false;
  }
}

async function extractPay100() {
  if (!canExtractPay100.value) return;
  pay100ExtractLoading.value = true;
  try {
    const res = await migrationImportService.extractPay100({
      data_file: pay100DataFile.value,
      tax_file: pay100TaxFile.value,
      password: pay100Password.value,
      companyId: effectiveCompanyId.value,
    });
    pay100ExtractSummary.value = res.data?.summary || null;
    sourceCompanyProfile.value = res.data?.company_profile || null;
    pay100Artifacts.value = Array.isArray(res.data?.artifacts) ? res.data.artifacts : [];
    toast("Pay100 extraction completed.", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to extract Pay100 data.", "error");
  } finally {
    pay100ExtractLoading.value = false;
  }
}

async function previewPay100Artifact(artifact) {
  if (!artifact?.module || !artifact?.artifact_path) return;
  previewLoading.value = true;
  try {
    const res = await migrationImportService.previewPay100Artifact({
      module: artifact.module,
      import_mode: importMode.value,
      artifact_path: artifact.artifact_path,
      companyId: effectiveCompanyId.value,
    });
    selectedModule.value = artifact.module;
    previewBatch.value = res.data;
    toast(`${artifact.label} preview generated.`, "success");
    await loadHistory();
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to preview extracted artifact.", "error");
  } finally {
    previewLoading.value = false;
  }
}

async function downloadPay100Artifact(artifact) {
  try {
    const res = await migrationImportService.downloadPay100Artifact(
      artifact.artifact_path,
      effectiveCompanyId.value
    );
    const blob = new Blob([res.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = artifact.file_name || "artifact.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to download artifact.", "error");
  }
}

async function commitImport() {
  if (!previewBatch.value?.id) return;
  commitLoading.value = true;
  try {
    const res = await migrationImportService.commit(
      previewBatch.value.id,
      effectiveCompanyId.value
    );
    previewBatch.value = res.data;
    toast("Import finished.", "success");
    await loadHistory();
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to commit import.", "error");
  } finally {
    commitLoading.value = false;
  }
}

function clearPreview() {
  previewBatch.value = null;
  selectedFile.value = null;
}

function clearPay100Extraction() {
  pay100ExtractSummary.value = null;
  pay100Artifacts.value = [];
  sourceCompanyProfile.value = null;
  companyProfileFieldSelection.value = {};
  pay100DataFile.value = null;
  pay100TaxFile.value = null;
  pay100Password.value = "";
}

async function repairRolePermissions() {
  if (!effectiveCompanyId.value) return;

  roleRepairLoading.value = true;
  roleRepairResult.value = null;

  try {
    const res = await migrationImportService.repairRolePermissions({
      companyId: effectiveCompanyId.value,
      useCentral: isSuperAdminRoute.value,
    });
    roleRepairResult.value = res.data || null;
    toast(res.data?.message || "Role permissions repaired.", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to repair role permissions.", "error");
  } finally {
    roleRepairLoading.value = false;
  }
}

async function applySourceCompanyProfile() {
  if (!effectiveCompanyId.value || !hasSourceCompanyProfile.value || !selectedComparisonKeys.value.length) return;

  applyingSourceCompanyProfile.value = true;
  try {
    const fd = new FormData();
    selectedComparisonKeys.value.forEach((key) => {
      const value = sourceCompanyProfile.value?.[key];
      if (value !== null && value !== undefined && value !== "") {
        fd.append(key, value);
      }
    });

    await companyStore.updateCompany(effectiveCompanyId.value, fd, true, effectiveCompanyId.value);
    await loadTargetCompany();
    toast("Target company updated from source company details.", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to apply source company details.", "error");
  } finally {
    applyingSourceCompanyProfile.value = false;
  }
}

function labelForModule(module) {
  return modules.value[module]?.label || module;
}

function statusClass(status) {
  if (status === "imported") return "bg-success";
  if (status === "imported_with_errors") return "bg-warning text-dark";
  if (status === "previewed") return "bg-info text-dark";
  return "bg-secondary";
}

function actionClass(action) {
  if (action === "create") return "bg-success";
  if (action === "update") return "bg-primary";
  if (action === "skip") return "bg-warning text-dark";
  return "bg-danger";
}

function summarizeRow(data = {}) {
  return Object.entries(data)
    .filter(([, value]) => value !== null && value !== "")
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${value}`)
    .join(" • ");
}

function summarizeHistory(summary = {}) {
  const parts = [];
  if (summary.total_rows != null) parts.push(`rows ${summary.total_rows}`);
  if (summary.imported_rows != null) parts.push(`imported ${summary.imported_rows}`);
  if (summary.updated_rows != null) parts.push(`updated ${summary.updated_rows}`);
  if (summary.failed_rows != null) parts.push(`failed ${summary.failed_rows}`);
  if (summary.skip_rows != null) parts.push(`skipped ${summary.skip_rows}`);
  return parts.join(" • ");
}

function hasComparableValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function formatComparisonValue(value) {
  return hasComparableValue(value) ? String(value) : "-";
}

function initializeCompanyProfileSelections() {
  const nextSelection = {};

  companyProfileFields.forEach(({ key }) => {
    const sourceValue = sourceCompanyProfile.value?.[key];
    const currentValue = currentTargetCompany.value?.[key];

    nextSelection[key] =
      hasComparableValue(sourceValue) && !hasComparableValue(currentValue);
  });

  companyProfileFieldSelection.value = nextSelection;
}

function formatDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

onMounted(async () => {
  if (isSuperAdmin.value && !companyStore.companies.length) {
    await companyStore.fetchCompanies({ all: true });
  }
  if (route.query.company_id) {
    targetCompanyId.value = Number(route.query.company_id);
  } else if (isSuperAdmin.value && !targetCompanyId.value && companyStore.activeCompanyId) {
    targetCompanyId.value = companyStore.activeCompanyId;
  }
  await loadMeta();
  await loadTargetCompany();
  await loadHistory();
});

watch(effectiveCompanyId, async () => {
  roleRepairResult.value = null;
  await loadTargetCompany();
  await loadHistory();
});

watch(sourceCompanyProfile, () => {
  initializeCompanyProfileSelections();
});
</script>

<style scoped>
.dq-migration-hero {
  background:
    radial-gradient(circle at top right, rgba(255, 123, 0, 0.14), transparent 32%),
    linear-gradient(135deg, #fffaf4 0%, #ffffff 100%);
}

.dq-kicker {
  display: inline-block;
  margin-bottom: 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #d16815;
}

.dq-order-card {
  min-width: 220px;
  padding: 1rem 1.1rem;
  border: 1px solid #f0dfcf;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
}

.dq-dropzone {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border: 1px dashed #d9c3af;
  border-radius: 16px;
  background: #fffaf6;
}

.dq-dropzone.is-ready {
  border-color: #198754;
  background: #f7fffa;
}

.dq-dropzone--stack {
  min-height: 120px;
  justify-content: flex-start;
  align-items: flex-start;
  cursor: pointer;
}

.dq-dropzone-title {
  font-weight: 600;
  color: #1f2937;
}

.dq-dropzone-help {
  font-size: 0.875rem;
  color: #6b7280;
}

.dq-summary-card {
  height: 100%;
  padding: 1rem;
  border: 1px solid #ece7df;
  border-radius: 14px;
  background: #fff;
}

.dq-summary-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
}

.dq-summary-value {
  margin-top: 0.35rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}
</style>
